import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';

import type { RequisitionNotificationRecipient } from '@customTypes/requisition';

import './RequisitionNotifications.scss';

export interface RequisitionNotificationsCardProps {
  requisitionId: string;
  recipients: RequisitionNotificationRecipient[];
  dirty: boolean;
  saving: boolean;
  savedFlash: boolean;
  loading?: boolean;
  errorMessage?: string | null;
  hideHeader?: boolean;
  onToggleRecipient: (providerId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onCancel: () => void;
  onSave: () => void;
}

const HELP_TOOLTIP =
  'Only providers who have access to this order and have email notifications turned on are listed. ' +
  'To add someone, share the order with them and have them turn on email notifications in their settings.';

export interface RequisitionNotificationsHelpIconProps {
  requisitionId: string;
}

export function RequisitionNotificationsHelpIcon(props: RequisitionNotificationsHelpIconProps) {
  const { requisitionId } = props;

  return (
    <OverlayTrigger
      placement='top'
      overlay={<Tooltip id={`requisition-notifications-help-${requisitionId}`}>{HELP_TOOLTIP}</Tooltip>}
    >
      <FontAwesomeIcon
        icon={faCircleQuestion as IconDefinition}
        className='text-muted cursor-pointer'
        aria-label='Who appears in this list'
      />
    </OverlayTrigger>
  );
}

export function RequisitionNotificationsCard(props: RequisitionNotificationsCardProps) {
  const {
    requisitionId,
    recipients,
    dirty,
    saving,
    savedFlash,
    loading = false,
    errorMessage = null,
    hideHeader = false,
    onToggleRecipient,
    onSelectAll,
    onDeselectAll,
    onCancel,
    onSave,
  } = props;

  const selectedCount = recipients.filter(recipient => recipient.selected).length;
  const listInteractive = !saving;

  if (loading) {
    return (
      <div
        className='requisition-notifications'
        data-testid='requisitionNotifications'
        aria-busy='true'
        aria-live='polite'
      >
        <div className='requisition-notifications__loading'>
          <Spinner animation='border' size='sm' role='status' />
        </div>
      </div>
    );
  }

  return (
    <div className='requisition-notifications' data-testid='requisitionNotifications'>
      {hideHeader ? null : (
        <div className='requisition-notifications__header'>
          <div className='requisition-notifications__title-block'>
            <div className='d-flex align-items-center gap-2'>
              <div className='requisition-notifications__title'>Notifications</div>
              <RequisitionNotificationsHelpIcon requisitionId={requisitionId} />
            </div>
          </div>
        </div>
      )}

      {errorMessage ? (
        <div className='requisition-notifications__error' role='alert'>
          {errorMessage}
        </div>
      ) : null}

      <div className='requisition-notifications__toolbar'>
        <Button type='button' size='sm' variant='outline-primary' onClick={onSelectAll} disabled={saving}>
          Select all
        </Button>
        <Button type='button' size='sm' variant='outline-secondary' onClick={onDeselectAll} disabled={saving}>
          Deselect all
        </Button>
        <span className='requisition-notifications__count ms-auto'>
          {selectedCount} of {recipients.length} selected
        </span>
      </div>

      <div className='requisition-notifications__list'>
        {recipients.map(recipient => (
          <div key={recipient.provider_id} className='requisition-notifications__row'>
            <Form.Check
              type='checkbox'
              id={`requisition-notifications-recipient-${requisitionId}-${recipient.provider_id}`}
              className='requisition-notifications__check'
              checked={recipient.selected}
              disabled={!listInteractive}
              onChange={() => onToggleRecipient(recipient.provider_id)}
              label={
                <span className='requisition-notifications__recipient'>
                  <span className='requisition-notifications__name'>{recipient.name}</span>
                  <span className='requisition-notifications__email'>{recipient.email}</span>
                </span>
              }
            />
          </div>
        ))}
      </div>

      <div className='requisition-notifications__footer'>
        {savedFlash ? (
          <span className='requisition-notifications__saved'>
            <FontAwesomeIcon icon={faCircleCheck as IconDefinition} className='me-1' aria-hidden />
            Saved
          </span>
        ) : null}
        {dirty ? (
          <>
            <span className='requisition-notifications__unsaved'>Unsaved changes</span>
            <span className='ms-auto d-flex gap-2'>
              <Button
                type='button'
                size='sm'
                variant='link'
                className='text-secondary'
                onClick={onCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type='button' size='sm' variant='primary' onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </span>
          </>
        ) : (
          <>
            <span className='requisition-notifications__summary'>{selectedCount} will be notified</span>
            <Button type='button' size='sm' variant='secondary' className='ms-auto' disabled>
              Save
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
